import React, { useState, useRef, useEffect } from "react";

interface DateTimeInputProps {
  value: string; // ISO string or datetime-local format
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  className?: string;
  mode?: "datetime" | "date" | "time";
}

// Calendar Icon
const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Clock Icon
const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Format display value
const formatDisplayValue = (value: string, mode: "datetime" | "date" | "time"): string => {
  if (!value) return "";

  try {
    // For datetime-local format (YYYY-MM-DDTHH:mm), parse directly
    let date: Date;
    if (value.includes("T") && !value.includes("Z")) {
      // datetime-local format - parse as local time
      date = new Date(value);
    } else {
      date = new Date(value);
    }

    if (isNaN(date.getTime())) return value;

    const options: Intl.DateTimeFormatOptions = {};

    if (mode === "date" || mode === "datetime") {
      options.day = "2-digit";
      options.month = "short";
      options.year = "numeric";
    }

    if (mode === "time" || mode === "datetime") {
      options.hour = "2-digit";
      options.minute = "2-digit";
    }

    return date.toLocaleDateString("en-GB", options);
  } catch {
    return value;
  }
};

export default function DateTimeInput({
  value,
  onChange,
  label,
  required,
  placeholder,
  minDate,
  maxDate,
  className = "",
  mode = "datetime",
}: DateTimeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    setDisplayValue(formatDisplayValue(value, mode));
  }, [value, mode]);

  const getInputType = () => {
    switch (mode) {
      case "date": return "date";
      case "time": return "time";
      default: return "datetime-local";
    }
  };

  const getIcon = () => {
    if (mode === "time") return <ClockIcon />;
    return <CalendarIcon />;
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (mode) {
      case "date": return "Selectează data";
      case "time": return "Selectează ora";
      default: return "Selectează data și ora";
    }
  };

  // Handle click on the container to focus the input (fallback for some browsers)
  const handleContainerClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      // Some mobile browsers need showPicker() to be called
      if (typeof inputRef.current.showPicker === "function") {
        try {
          inputRef.current.showPicker();
        } catch {
          // showPicker may fail if not triggered by user gesture, ignore
        }
      }
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {/* Container that wraps both the display and the input */}
      <div
        className="relative cursor-pointer"
        onClick={handleContainerClick}
      >
        {/* Visible styled container - non-interactive */}
        <div className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-left text-slate-800 flex items-center gap-3 pointer-events-none">
          <span className="text-emerald-500">{getIcon()}</span>
          <span className={displayValue ? "text-slate-800" : "text-slate-400"}>
            {displayValue || getPlaceholder()}
          </span>
        </div>

        {/* Native input - positioned absolutely but with minimal opacity for better touch handling */}
        <input
          ref={inputRef}
          type={getInputType()}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={minDate}
          max={maxDate}
          required={required}
          className="absolute inset-0 w-full h-full cursor-pointer"
          aria-label={label || getPlaceholder()}
          style={{
            opacity: 0.01, // Very low opacity instead of 0 for better mobile touch handling
            WebkitAppearance: "none",
            MozAppearance: "none",
          }}
        />
      </div>
    </div>
  );
}

// Simplified inline versions for quick use
export function DateInput(props: Omit<DateTimeInputProps, "mode">) {
  return <DateTimeInput {...props} mode="date" />;
}

export function TimeInput(props: Omit<DateTimeInputProps, "mode">) {
  return <DateTimeInput {...props} mode="time" />;
}
